import LeadScore from '@/features/leads/LeadScore'
import { liveEntries } from '@/features/leads/workflow/timeline'
import { cloneElement, useEffect, useState } from 'react'
import { countryOf, formatPhone } from '@/shared/lib/contact'
import { fmtDate } from '@/shared/lib/format'
import { cls } from '@/ui/cls'
import { Ico } from '@/ui/Ico'
import Avatar from '@/shared/Avatar'
import CopyButton from './CopyButton'
import EmailDot from './EmailDot'
import ImportanceFlag from './ImportanceFlag'
import StatusStrip from './StatusStrip'
import SalesforceMark from '@/features/leads/SalesforceMark'
import SourceChip from '@/features/leads/SourceMark'
import ContactEditor, { type ContactFields } from './ContactEditor'
import ContactDuplicates from './ContactDuplicates'
import { cityOf } from '@/shared/regions'
import { INTERACTION_KINDS } from '@/shared/domain'
import { CompanyFactsStrip } from './CompanyIntel'
import type { Lead, HandoverContact } from '@/shared/types'
import { isStartScore } from '@/features/leads/score'
import { hasAiBrief, hasAiReasoning } from './aiBrief'
import { domainOf, signalKindLabel, signalSource } from './signals'
import { AnimatePresence } from 'motion/react'
import { useBrand } from '@/shared/white-label/useBrand'
import { useCanEdit } from '@/shared/write-access'
import { useLeadWrites } from '@/features/leads/workflow/useLeadMutations'
import { censorHandover } from '@/shared/white-label/anonymise'
import { Eyebrow } from '@/ui'
import { Badge } from '@/components/ui/badge'
import { useLeadSalesforce } from '@/shared/api/leads'
import {
  accountBadgeText, hasSalesforceFacts, ownerFieldLabel, ownerFieldTitle, parentBadgeText,
  resolveSalesforceView, type SalesforceView,
} from './salesforceView'

interface Props {
  lead: Lead
  // `mode` is accepted for API compatibility with the few call sites that
  // still pass it (FocusView / LeadOverlay); the new Tailwind layout no
  // longer has per-mode style branches.
  mode?: 'queue' | 'drawer' | 'canvas' | 'full'
  // When provided, each contact row gets edit/delete affordances and an
  // "Add contact" button appears at the end of the decision-makers grid.
  // Identity-less rows (handover-only contacts with `id == null`) skip the
  // edit/delete buttons — they're not addressable by the backend yet.
}

// Stable identifier for a contact. Prefers the row's `id` (UUID or
// `local:<uuid>`) so that BDR edits to email/name don't break a previously-
// set primary_contact_override. Falls back to the legacy email-or-name key
// for handover-only contacts that have no id yet.
function contactKey(c: HandoverContact): string {
  if (c.id) return c.id
  const email = (c.email || '').trim().toLowerCase()
  if (email) return email
  return (c.name || '').trim().toLowerCase()
}

interface LastContactInfo {
  at: string
  by: string
  source: 'sf' | 'radar'
}

// Pick whichever is more recent between Salesforce's last-activity stamp
// and the most recent Radar interaction in the local timeline. Returns
// null when nobody has ever touched this lead — caller hides the row.
// This is the decisive "don't double-call" signal from Ebba's feedback
// (RADAR-SE-006).
function lastContactInfo(lead: Lead): LastContactInfo | null {
  const sfAt = lead.sfLastActivityAt || null
  const radarLast = [...liveEntries(lead.timeline)].reverse()
    .find(t => INTERACTION_KINDS.has(t.kind))
  const sfTime = sfAt ? Date.parse(sfAt) : 0
  const radarTime = radarLast?.at ? Date.parse(radarLast.at) : 0
  if (!sfTime && !radarTime) return null
  if (sfTime >= radarTime && sfAt) {
    return { at: sfAt, by: lead.sfLeadOwner || 'Salesforce-användare', source: 'sf' }
  }
  if (radarLast?.at) {
    return { at: radarLast.at, by: radarLast.by || 'en BDR', source: 'radar' }
  }
  return null
}

function relativeAge(iso: string): string {
  const diffMs = Date.now() - Date.parse(iso)
  const days = Math.floor(diffMs / 86_400_000)
  if (days < 0) return 'i framtiden'
  if (days === 0) return 'idag'
  if (days === 1) return 'igår'
  if (days < 30) return `${days} dagar sedan`
  const months = Math.floor(days / 30)
  if (months < 12) return `${months} månad${months === 1 ? '' : 'er'} sedan`
  const years = Math.floor(days / 365)
  return `${years} år sedan`
}

function StarButton({
  filled, label, onClick,
}: {
  filled: boolean
  label: string
  onClick: () => void
}) {
  const iconEl = filled ? Ico.starFilled : Ico.star
  const icon = cloneElement(iconEl, { width: 14, height: 14 })
  return (
    <button
      type="button"
      className={cls(
        'inline-flex items-center justify-center w-[26px] h-[26px] p-0',
        'rounded-[2px] cursor-pointer border',
        'transition-[color,background,border-color] duration-[120ms] ease-in-out',
        filled
          ? 'text-ink-1000 bg-brand border-brand hover:bg-brand-deep'
          : 'text-ink-500 bg-transparent border-ink-200 hover:text-ink-1000 hover:border-ink-500',
      )}
      onClick={(e) => { e.stopPropagation(); onClick() }}
      aria-label={label}
      title={label}
    >
      {icon}
    </button>
  )
}

// "edited" pip — quiet visual cue that a row has BDR overrides on top of the
// canonical contact data. Hovered → reads "Edited by BDR".
function EditedPip() {
  return (
    <span
      className="inline-flex items-center text-[9px] tracking-widest uppercase font-bold px-1.5 py-0.5 bg-ink-100 text-ink-700 rounded-[2px]"
      title="Redigerad av BDR — lokalt i CRM:et, synkas inte till Salesforce"
    >
      Redigerad
    </span>
  )
}

function ContactSource({ contact, compact }: { contact: HandoverContact; compact?: boolean }) {
  if (!contact.sourceUrl) return null
  const linkIcon = cloneElement(Ico.link, { width: compact ? 10 : 11, height: compact ? 10 : 11 })
  return (
    <a
      className={cls(
        'inline-flex items-center gap-1 text-ink-500 hover:text-ink-1000',
        compact ? 'text-[10px] mt-0.5' : 'text-[11px] mt-1',
      )}
      href={contact.sourceUrl}
      target="_blank"
      rel="noreferrer"
      title={contact.sourceUrl}
    >
      {linkIcon} {domainOf(contact.sourceUrl)}
    </a>
  )
}

// Eyebrow utility — small uppercase muted label used in section headers.
const eyebrowCls = 'text-[10px] tracking-widest uppercase text-ink-500 inline-flex items-center gap-2 font-medium [&_svg]:w-[11px] [&_svg]:h-[11px]'
const eyebrowMetaCls = 'text-[11px] text-ink-500 font-mono'
const sectionHeadCls = 'flex items-center justify-between px-7 pt-4 pb-2'
// Read-only greys the add buttons out with this reason rather than hiding them.
const ADD_CONTACT_READ_ONLY_TITLE = 'Aktivera redigering för att lägga till kontakter'

export default function HandoverCard({
  lead,
  mode: _mode = 'full',
}: Props) {
  const { setPrimaryContact, patchContact, addContact, deleteContact, writeError } = useLeadWrites()
  // Was `!!onPatchContact` — the callbacks were always supplied, so it always
  // read true, and the contact editors stayed on screen in read-only mode doing
  // nothing when clicked. This is the flag that check was standing in for.
  const canEdit = useCanEdit()
  const { brand, person, anonymised } = useBrand()
  const { data: liveSf } = useLeadSalesforce(lead.id)
  const sf = resolveSalesforceView(lead, liveSf)
  // Censored as a whole rather than field by field — see censorHandover.
  const h = anonymised ? censorHandover(lead.handover, brand.name) : lead.handover
  const startScore = isStartScore(lead)
  const aiBrief = hasAiBrief(h)
  const primaryKey = contactKey(h.primary)
  const hasOverride = !!lead.primaryContactOverride
  const promote = (c: HandoverContact) => {
    setPrimaryContact(lead.id, contactKey(c), c.name)
  }
  const clearOverride = () => {
    setPrimaryContact(lead.id, null, h.primary.name)
  }
  const boltIcon = cloneElement(Ico.bolt, { width: 11, height: 11 })
  const lastContact = lastContactInfo(lead)
  const cityName = cityOf(lead.location)
  const hasCity = !!cityName
  // The backend sends one of these two sentinels (never an empty string) when
  // the lead has no contact at all — "(unknown)" when there were no contact
  // rows and no AI-picked primary, "(unnamed)" when a handover-only contact
  // had no name field (see _split_contacts / _normalize_contact in the API).
  const noPrimary = !h.primary.name || h.primary.name === '(unknown)' || h.primary.name === '(unnamed)'
  const noPhone = !noPrimary && !h.primary.phone
  // Kontakta ej: numbers stay readable and copyable, but nothing on the card dials them.
  const dialable = !lead.doNotContact

  // Editor state — `editing` holds the contact id being edited, or the
  // literal '__add__' when the BDR is creating a new contact. The editor
  // renders as a modal so the card itself never has to make room for the
  // form (the inline approach squeezed contact rows even on hover).
  const [editing, setEditing] = useState<string | null>(null)
  const [confirmingDelete, setConfirmingDelete] = useState<string | null>(null)
  const [removing, setRemoving] = useState(false)
  const [failed, setFailed] = useState('')
  const cancelEdit = () => setEditing(null)
  const pendingDelete = confirmingDelete
    ? [h.primary, ...h.decisionMakers].find(c => contactKey(c) === confirmingDelete) || null
    : null
  // Nothing keys this component on the lead, so advancing the queue does not
  // remount it — a pending confirmation or a failure message would otherwise
  // follow the BDR onto the next company's card.
  useEffect(() => {
    setEditing(null)
    setConfirmingDelete(null)
    setFailed('')
  }, [lead.id])

  // A contact the AI brief supplied on its own has no contacts row, so it
  // reaches the card with `id == null` and the backend has no key to PATCH.
  // Editing one adopts it instead: the editor opens prefilled and the save
  // POSTs a CRM-local contact. The merge then resolves the brief's primary
  // onto that new row (matched on email, then name), so the card shows one
  // person rather than the original and its copy.
  const adopting = editing === '__adopt__'
  const editingContact = editing && editing !== '__add__' && !adopting
    ? [h.primary, ...h.decisionMakers].find(c => c.id && c.id === editing) || null
    : null

  // Save handlers close the modal SYNCHRONOUSLY before firing the mutation —
  // the BDR sees the dialog disappear immediately and the contact card
  // updates when the PATCH/POST/DELETE response splices into the cache
  // (typically ~150ms later). Failures land in the inline message below the
  // contacts so a failed save doesn't silently look successful. The previous
  // await-then-close pattern sat in "Saving…" for the full round-trip and
  // read as sluggish.
  const saveEdit = (key: string) => async (fields: Partial<ContactFields>) => {
    if (!canEdit) return
    setEditing(null)
    setFailed('')
    try {
      await patchContact(lead.id, key, fields)
    } catch (err) {
      setFailed(err instanceof Error ? err.message : "Kunde inte uppdatera kontakten. Försök igen.")
    }
  }
  const saveAdd = async (fields: Partial<ContactFields>) => {
    if (!canEdit || !fields.name) return
    setEditing(null)
    setFailed('')
    try {
      await addContact(lead.id, fields as Partial<ContactFields> & { name: string })
    } catch (err) {
      setFailed(err instanceof Error ? err.message : "Kunde inte lägga till kontakten. Försök igen.")
    }
  }
  const askRemoveContact = (key: string) => {
    setEditing(null)
    setFailed('')
    setConfirmingDelete(key)
  }
  const removeContact = async (key: string) => {
    if (!canEdit || removing) return
    setFailed('')
    setRemoving(true)
    try {
      await deleteContact(lead.id, key)
      setConfirmingDelete(null)
    } catch (err) {
      setFailed(err instanceof Error ? err.message : "Kunde inte ta bort kontakten. Försök igen.")
    } finally {
      setRemoving(false)
    }
  }
  return (
    // `handover` class is kept to preserve the existing mount animation
    // (app-fade-in-up via app.css). The `handover-${mode}` modifier isn't
    // referenced anywhere, dropping it.
    <article className={cls('handover bg-white flex flex-col')}>
      {/* STATUS STRIP — what's the situation right now? */}
      <StatusStrip lead={lead} />
      {writeError && <p role="alert" className="px-7 py-2 text-xs text-[var(--color-danger)]">{writeError}</p>}

      {/* HEADER — Company + Primary contact (unified) + Score */}
      <header className="grid grid-cols-[1fr_auto] gap-6 px-7 pt-5 pb-[18px]">
        <div className="min-w-0">
          <h1 className="font-display text-[28px] font-medium leading-[1.1] tracking-tighter m-0 inline-flex items-center gap-2.5 text-ink-1000">
            <span>{lead.company}</span>
            {hasCity && (
              <span
                className="inline-flex items-center gap-1 text-[12px] font-normal tracking-normal text-ink-700 bg-ink-50 border border-ink-200 rounded-full px-2 py-0.5"
                title="Ort"
              >
                <span className="text-ink-500 inline-flex">{cloneElement(Ico.pin, { width: 12, height: 12 })}</span>
                {cityName}
              </span>
            )}
            <CopyButton value={lead.company} label={`Kopiera "${lead.company}"`} />
            <SourceChip source={lead.source} />
          </h1>

          {hasSalesforceFacts(sf) && <SalesforceStrip sf={sf} person={person} />}

          {lastContact && (
            <div
              className={cls(
                'mt-2 flex w-fit items-center gap-1.5 border-l-2 bg-ink-50 px-2.5 py-1.5 text-[12px] text-ink-700',
                lastContact.source === 'sf' ? 'border-l-[#00A1E0]' : 'border-l-ink-200',
              )}
              title={`Senaste aktivitet på den här leaden — ${fmtDate(lastContact.at, { time: true })}`}
            >
              Senaste kontakt{' '}
              <span className="text-ink-1000 font-medium">{fmtDate(lastContact.at)}</span>{' '}
              <span className="text-ink-500">({relativeAge(lastContact.at)})</span>{' '}
              av{' '}
              <span className="text-ink-1000 font-medium">{person(lastContact.by)}</span>{' '}
              <span className="text-ink-500">
                ({lastContact.source === 'sf' ? 'Salesforce' : brand.documentTitle})
              </span>
            </div>
          )}

          {noPrimary ? (
            <div className="flex items-center gap-3 mt-2.5 px-3 py-2.5 border border-dashed border-ink-200 rounded-[2px] bg-ink-50">
              <span className="text-xs text-ink-700">Ingen kontakt hittad på det här företaget än.</span>
              <button
                type="button"
                onClick={() => setEditing('__add__')}
                disabled={!canEdit}
                title={canEdit ? undefined : ADD_CONTACT_READ_ONLY_TITLE}
                className="ml-auto shrink-0 text-xs font-semibold text-ink-1000 bg-white border border-ink-200 rounded-[2px] px-2.5 py-1 cursor-pointer enabled:hover:border-ink-1000 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Lägg till kontakt
              </button>
            </div>
          ) : (
            <div className="flex items-start gap-3 mt-2.5">
              <Avatar name={h.primary.name} size={36} tone="yellow" />
              <div className="flex-1 min-w-0">
                <div className="text-[15px] font-semibold text-ink-1000 flex items-center gap-2 flex-wrap">
                  <span>{h.primary.name}</span>
                  <CopyButton value={h.primary.name} label={`Kopiera "${h.primary.name}"`} />
                  {hasOverride && (
                    <span className="inline-flex items-center text-[9px] tracking-widest uppercase font-bold px-1.5 py-0.5 bg-brand text-ink-1000 rounded-[2px]">
                      BDR-vald
                    </span>
                  )}
                  {h.primary.edited && <EditedPip />}
                  {canEdit && hasOverride && (
                    <StarButton
                      filled
                      label="Återställ till AI-vald kontakt"
                      onClick={clearOverride}
                    />
                  )}
                </div>
                {h.primary.role && <div className="text-xs text-ink-500 mt-0.5">{h.primary.role}</div>}
                <div className="flex flex-wrap gap-x-3.5 gap-y-1 mt-1.5 text-xs [&_a]:inline-flex [&_a]:items-center [&_a]:gap-1.5 [&_a]:text-ink-700 [&_a:hover]:text-ink-1000 [&_a]:whitespace-nowrap [&_a_svg]:w-3 [&_a_svg]:h-3">
                  <span className="inline-flex items-center">
                    <a href={`mailto:${h.primary.email}`}>
                      <EmailDot status={h.primary.emailStatus} /> {h.primary.email}
                    </a>
                    <CopyButton value={h.primary.email} label="Kopiera e-post" />
                  </span>
                  {h.primary.phone && (
                    <span className="inline-flex items-center">
                      <a
                        href={dialable ? `tel:${h.primary.phone}` : undefined}
                        title={dialable ? undefined : 'Kontakta ej'}
                      >
                        {Ico.phone} {formatPhone(h.primary.phone, countryOf(lead.location))}
                      </a>
                      <CopyButton
                        value={formatPhone(h.primary.phone, countryOf(lead.location))}
                        label="Kopiera telefonnummer"
                      />
                    </span>
                  )}
                  {h.primary.phone && dialable && (
                    // SMS sibling of the tel: link — macOS Messages picks the
                    // sms: scheme up; PC browsers ignore it. Andreas explicitly
                    // accepted PC users not getting the action (YLLW-CRM-019).
                    <a
                      href={`sms:${h.primary.phone}`}
                      title={`Skicka SMS till ${formatPhone(h.primary.phone, countryOf(lead.location))}`}
                    >
                      {Ico.chat} SMS
                    </a>
                  )}
                  {noPhone && (
                    <span className="inline-flex items-center gap-1.5 text-ink-500">
                      Inget telefonnummer för {h.primary.name}.
                      {canEdit && (
                        <button
                          type="button"
                          onClick={() => setEditing(h.primary.id ?? '__adopt__')}
                          className="font-semibold text-ink-1000 underline underline-offset-2 bg-transparent border-0 p-0 cursor-pointer"
                        >
                          Byt kontakt
                        </button>
                      )}
                    </span>
                  )}
                  {h.primary.linkedin && (() => {
                    const raw = h.primary.linkedin.trim().replace(/^\/+/, '')
                    const href = /^https?:\/\//i.test(raw)
                      ? raw
                      : /^linkedin\.com\//i.test(raw)
                        ? `https://${raw}`
                        : `https://linkedin.com/${raw}`
                    // "LinkedIn" reads as a channel — same syntactic weight as
                    // "viktoria@…" and the phone number. Used to render the
                    // URL fragment (in/handle), which read as a hyperlink
                    // artifact instead of a peer channel (YLLW-CRM-018).
                    return (
                      <span className="inline-flex items-center">
                        <a href={href} target="_blank" rel="noopener noreferrer" title={href}>
                          {Ico.linkedin} LinkedIn
                        </a>
                        <CopyButton value={href} label="Kopiera LinkedIn-URL" />
                      </span>
                    )
                  })()}
                  {canEdit && (
                    <button
                      type="button"
                      onClick={() => setEditing(h.primary.id ?? '__adopt__')}
                      title="Redigera kontakt"
                      className="inline-flex items-center gap-1.5 text-ink-500 hover:text-ink-1000 bg-transparent border-0 p-0 cursor-pointer font-[inherit] text-xs"
                    >
                      {cloneElement(Ico.edit, { width: 12, height: 12 })}
                      Redigera
                    </button>
                  )}
                </div>
                {h.primary.note && <div className="text-xs text-ink-500 mt-2 italic">↳ {h.primary.note}</div>}
                <ContactSource contact={h.primary} />
              </div>
            </div>
          )}

          <ContactDuplicates leadId={lead.id} />

          {lead.orgNo && (
            <div className="mt-3 text-[11px] text-ink-500 font-mono tracking-[0.02em]">
              Orgnr · {lead.orgNo}
              <CopyButton value={lead.orgNo} label="Kopiera orgnr" />
            </div>
          )}
        </div>

        <LeadScore lead={lead} prominent />
      </header>

      <CompanyFactsStrip lead={lead} />

      {/* AI BRIEF — Opportunity / Pitch / Timing. A manual or imported start
          score has no AI brief: it reads as "no AI verdict yet". */}
      {!startScore && (
      <section className="border-b border-ink-200">
        <header className={sectionHeadCls}>
          <span className={eyebrowCls}>{boltIcon} AI-SAMMANFATTNING</span>
          {aiBrief && (
            <span className={eyebrowMetaCls}>
              Genererad {fmtDate(lead.leadDate)} · {brand.radar}
            </span>
          )}
        </header>
        {!aiBrief ? (
          <p className="px-7 pb-5 text-[13px] text-ink-500 m-0">Ingen AI-sammanfattning ännu</p>
        ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 px-7 pb-5">
          <div className="pr-4 border-r border-ink-200">
            <Eyebrow size="sm" gap="sm">Möjlighet</Eyebrow>
            <p className="text-[13px] leading-[1.55] text-ink-1000 m-0">{h.opportunity}</p>
          </div>
          <div className="px-4 border-r border-ink-200">
            <Eyebrow size="sm" gap="sm">Säljvinkel</Eyebrow>
            <p className="text-[13px] leading-[1.55] text-ink-1000 m-0">{h.pitchAngle}</p>
          </div>
          <div className="pl-4 -mr-4 px-4 py-2.5 bg-brand/[0.08] relative">
            <Eyebrow size="sm" gap="sm">Tajming</Eyebrow>
            <p className="text-[13px] leading-[1.55] text-ink-1000 m-0">{h.timing}</p>
          </div>
        </div>
        )}
      </section>
      )}

      {/* SIGNALS — collapsed by default. ho-signals class is retained as a
          hook for the chevron pseudo-element kept in app.css. */}
      {h.signals.length > 0 && (
        <details className="ho-signals border-b border-ink-200">
          <summary className={cls(sectionHeadCls, 'cursor-pointer list-none relative pr-11 select-none hover:bg-ink-50')}>
            <span className={eyebrowCls}>⚐ VIKTIGA SIGNALER</span>
            <span className={eyebrowMetaCls}>{h.signals.length} identifierade</span>
          </summary>
          <div className="px-7 pb-[18px] flex flex-col">
            {h.signals.map((s, i) => {
              const src = signalSource(s)
              return (
                <div
                  key={i}
                  className="grid grid-cols-[70px_1fr_auto] gap-4 py-2.5 border-b border-ink-200 last:border-b-0"
                >
                  <ImportanceFlag level={s.importance} />
                  <div className="min-w-0">
                    <div className="text-xs font-semibold text-ink-1000 mb-px">{signalKindLabel(s.kind)}</div>
                    <div className="text-xs text-ink-700 leading-[1.45]">{s.summary}</div>
                  </div>
                  <div className="flex flex-col items-end gap-0.5">
                    <span className="font-mono text-[11px] text-ink-500">{fmtDate(s.date)}</span>
                    {src?.href ? (
                      <a
                        className="inline-flex items-center gap-1 text-[11px] text-ink-500 hover:text-ink-1000 [&_svg]:w-2.5 [&_svg]:h-2.5"
                        href={src.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={e => e.stopPropagation()}
                        title={src.href}
                      >
                        Källa: {src.site} {Ico.ext}
                      </a>
                    ) : src ? (
                      <span className="text-[11px] text-ink-500 cursor-default">Källa: {src.site}</span>
                    ) : null}
                  </div>
                </div>
              )
            })}
          </div>
        </details>
      )}

      {/* ESG (optional) */}
      {h.esg && (
        <section className="border-b border-ink-200 pb-3.5">
          <header className={sectionHeadCls}>
            <span className={cls(eyebrowCls, 'text-ink-1000')}>
              <span className="inline-block w-2 h-2 bg-brand mr-1" />◉ HÅLLBARHET · ESG
            </span>
          </header>
          <p className="px-7 text-[13px] leading-[1.6] text-ink-700 m-0">{h.esg}</p>
        </section>
      )}

      {/* DECISION MAKERS — supporting context, lower priority than the brief */}
      <section className="pb-5">
        <header className={sectionHeadCls}>
          <span className={eyebrowCls}>○ BESLUTSFATTARE</span>
          <span className={eyebrowMetaCls}>
            {h.decisionMakers.length === 0
              ? 'ingen i inköpskommittén än'
              : `${h.decisionMakers.length} i inköpskommittén`}
          </span>
        </header>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-ink-200 mx-7 border border-ink-200">
          {h.decisionMakers.map((d, i) => {
            const isPrimary = contactKey(d) === primaryKey
            return (
              <div
                key={i}
                className={cls(
                  'grid grid-cols-[auto_auto_1fr_auto] gap-2.5 items-center px-3 py-2 relative group',
                  isPrimary
                    ? 'bg-gradient-to-r from-brand/[0.06] to-white to-50% before:absolute before:left-0 before:top-0 before:bottom-0 before:w-0.5 before:bg-brand before:content-[""]'
                    : 'bg-white',
                )}
              >
                {canEdit && (
                  <StarButton
                    filled={isPrimary}
                    label={isPrimary ? 'Återställ till AI-vald kontakt' : `Gör ${d.name} till primär kontakt`}
                    onClick={() => (isPrimary ? clearOverride() : promote(d))}
                  />
                )}
                <Avatar name={d.name} size={32} tone="muted" />
                <div className="min-w-0">
                  {/* flex-wrap + min-w-0: inline-flex refuses to shrink below
                      its content, so once the contact cell claims its 200px
                      the EDITED pip overflowed the name column and landed on
                      top of the phone icon. Wrapping drops it to its own line
                      instead. */}
                  <div className="text-xs font-semibold text-ink-1000 flex flex-wrap items-center gap-1.5 min-w-0">
                    <span>{d.name}</span>
                    {d.edited && <EditedPip />}
                  </div>
                  <div className="text-[11px] text-ink-500">{d.role}</div>
                  <ContactSource contact={d} compact />
                </div>
                {/* Email over phone, both in the same cell so the row keeps
                    its four columns. The phone belongs here: without it a
                    BDR who corrects a number on anyone but the primary sees
                    the card come back unchanged and reads the save as lost
                    (YLLW-CRM-024). */}
                <div className="text-[11px] text-ink-700 font-mono max-w-[200px] overflow-hidden">
                  {/* The ellipsis goes on the span: on the flex row it never
                      reached the text, which was cut mid-address (YCRM-59). */}
                  <div className="flex items-center gap-1 min-w-0">
                    <EmailDot status={d.emailStatus} />
                    <span className="truncate" title={d.email || undefined}>{d.email}</span>
                  </div>
                  {d.phone && (
                    <div className="flex items-center gap-1 mt-0.5 whitespace-nowrap [&_svg]:w-3 [&_svg]:h-3">
                      <a
                        href={dialable ? `tel:${d.phone}` : undefined}
                        title={dialable ? undefined : 'Kontakta ej'}
                        className="inline-flex items-center gap-1 text-ink-700 hover:text-ink-1000"
                        onClick={e => e.stopPropagation()}
                      >
                        {Ico.phone} {formatPhone(d.phone, countryOf(lead.location))}
                      </a>
                      <CopyButton
                        value={formatPhone(d.phone, countryOf(lead.location))}
                        label={`Kopiera ${d.name}s telefonnummer`}
                      />
                    </div>
                  )}
                </div>
                {canEdit && d.id && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setEditing(d.id!) }}
                    title={`Redigera ${d.name}`}
                    aria-label={`Redigera ${d.name}`}
                    className={cls(
                      'absolute top-1 right-1 inline-flex items-center justify-center w-[22px] h-[22px] p-0',
                      'bg-white/95 border border-ink-200 rounded-[2px] cursor-pointer',
                      'text-ink-500 hover:text-ink-1000 hover:border-ink-1000',
                      'opacity-0 group-hover:opacity-100 transition-opacity',
                    )}
                  >
                    {cloneElement(Ico.edit, { width: 12, height: 12 })}
                  </button>
                )}
              </div>
            )
          })}
        </div>

        <div className="mx-7 mt-2">
          <button
            type="button"
            onClick={() => setEditing('__add__')}
            disabled={!canEdit}
            title={canEdit ? undefined : ADD_CONTACT_READ_ONLY_TITLE}
            className="w-full px-3 py-2 text-xs font-semibold text-ink-700 bg-transparent border border-dashed border-ink-200 rounded-[2px] cursor-pointer enabled:hover:border-ink-1000 enabled:hover:text-ink-1000 transition-colors disabled:cursor-not-allowed disabled:opacity-50"
          >
            + Lägg till kontakt
          </button>
        </div>
      </section>

      {(pendingDelete || failed) && (
        <div className="mx-7 pb-5">
          {pendingDelete ? (
            <div className="border border-ink-200 rounded-[2px] bg-ink-50 p-2.5">
              <div className="text-[12px] font-semibold text-ink-1000">
                Ta bort {pendingDelete.name} från den här leaden?
              </div>
              <ul className="text-[11px] text-ink-700 mt-1 mb-2 pl-4 list-disc leading-[1.6]">
                <li>Kontakten tas bort från leadens kontaktlista för alla i teamet.</li>
                <li>Borttagningen loggas i leadens tidslinje, med ditt namn på den.</li>
                <li>Inget ändras i Salesforce — den här listan är lokal för CRM:et.</li>
              </ul>
              {failed && <div className="text-[11px] text-danger mb-2">{failed}</div>}
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={removing}
                  className="px-2.5 py-1 text-[11px] font-semibold rounded-[2px] bg-ink-1000 text-white border-0 cursor-pointer disabled:opacity-50"
                  onClick={() => removeContact(confirmingDelete!)}
                >
                  {removing ? 'Tar bort…' : 'Ja, ta bort'}
                </button>
                <button
                  type="button"
                  className="px-2.5 py-1 text-[11px] rounded-[2px] border border-ink-200 bg-transparent text-ink-700 cursor-pointer"
                  onClick={() => { setConfirmingDelete(null); setFailed('') }}
                >
                  Behåll kontakten
                </button>
              </div>
            </div>
          ) : (
            <div className="text-[11px] text-danger">{failed}</div>
          )}
        </div>
      )}

      {/* AI REASONING */}
      {!startScore && hasAiReasoning(h) && (
      <section className="pb-5">
        <header className={sectionHeadCls}>
          <span className={eyebrowCls}>⌥ AI-RESONEMANG</span>
        </header>
        <p className="px-7 text-xs leading-[1.6] text-ink-700 m-0 text-pretty">
{h.reasoning}</p>
      </section>
      )}

      <AnimatePresence>
      {(editing === '__add__' || adopting) && canEdit && (
        <ContactEditor
          mode="add"
          adopting={adopting}
          initial={adopting ? h.primary : null}
          leadCompany={lead.company}
          onSave={saveAdd}
          onCancel={cancelEdit}
        />
      )}
      {editingContact && editingContact.id && canEdit && (
        <ContactEditor
          mode="edit"
          initial={editingContact}
          leadCompany={lead.company}
          onSave={saveEdit(editingContact.id)}
          onCancel={cancelEdit}
          onDelete={
            canEdit && contactKey(editingContact) !== primaryKey
              ? () => askRemoveContact(editingContact.id!)
              : undefined
          }
        />
      )}
      </AnimatePresence>
    </article>
  )
}

/**
 * The Salesforce facts a BDR needs before dialling, on the card rather than in
 * a tooltip: whether the lead is in Salesforce at all, what status it carries
 * there, and who — or what — owns it.
 *
 * The owner is the part worth reading twice. Nearly half of these leads are
 * owned by a shared queue rather than a colleague, so the label says which,
 * and a queue name is never run through the white-label person mask: masking
 * it would turn "Inbound SE" into a fake human being.
 */
function SalesforceStrip({ sf, person }: {
  sf: SalesforceView
  person: (value?: string | null) => string
}) {
  if (sf.uncheckedReason) {
    return (
      <div className="mt-1.5 inline-flex items-center gap-1.5 rounded border border-ink-200 bg-ink-50 px-2 py-1 text-[11px] text-ink-500">
        <SalesforceMark size={12} />
        {sf.uncheckedReason}
      </div>
    )
  }
  if (sf.present === false) {
    return (
      <div className="mt-2 inline-flex items-center gap-1.5 border-b border-ink-200 py-1 text-[11px] text-ink-500">
        <SalesforceMark size={12} />
        Inte i Salesforce
      </div>
    )
  }

  const ownerText = sf.owner
    ? (sf.ownerKind === 'queue' ? sf.owner : person(sf.owner))
    : null
  const parentText = sf.account && parentBadgeText(sf.account)

  return (
    <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 border-y border-ink-200 py-1.5 text-[11px]">
      {sf.link ? (
        <a
          className="inline-flex items-center gap-1.5 font-semibold tracking-wide text-[#0b76a3] no-underline hover:underline"
          href={sf.link}
          target="_blank"
          rel="noreferrer"
          title="Öppna den här leaden i Salesforce"
        >
          <SalesforceMark size={13} />
          I Salesforce
          <span aria-hidden="true">↗</span>
        </a>
      ) : (
        <span className="inline-flex items-center gap-1.5 font-semibold tracking-wide text-[#0b76a3]">
          <SalesforceMark size={13} />
          I Salesforce
        </span>
      )}

      {sf.account && (
        <Badge
          variant={sf.account.customer ? 'warning' : 'outline'}
          icon={null}
          title={sf.account.type ? `Kontotyp i Salesforce: ${sf.account.type}` : undefined}
          className="h-5 text-[10px]"
        >
          {accountBadgeText(sf.account, person)}
        </Badge>
      )}
      {parentText && (
        <Badge variant="outline" icon={null} className="h-5 border-ink-200 text-[10px] font-normal text-ink-700">
          {parentText}
        </Badge>
      )}
      {sf.status && <SfFact label="Status" value={sf.status} strong />}
      {ownerText && (
        <SfFact
          label={ownerFieldLabel(sf.ownerKind)}
          value={ownerText}
          title={ownerFieldTitle(sf.ownerKind, sf.owner || ownerText)}
          strong
          tag={sf.ownerKind === 'queue' ? 'kö' : undefined}
        />
      )}
      {sf.rating && <SfFact label="Betyg" value={sf.rating} />}
      {sf.lastActivityAt && (
        <SfFact
          label="Senaste SF-aktivitet"
          value={fmtDate(sf.lastActivityAt)}
          title={fmtDate(sf.lastActivityAt, { time: true })}
        />
      )}
      {sf.convertedAt && <SfFact label="Konverterad" value={fmtDate(sf.convertedAt)} />}
      {sf.openOpportunityStage && <SfFact label="Öppen affär" value={sf.openOpportunityStage} strong />}
      {!sf.live && (
        <Badge variant="outline" icon={null} title="Från senaste datasynken, den direkta Salesforce-läsningen har inte svarat än" className="h-5 border-ink-200 bg-ink-50 text-[10px] font-normal text-ink-500">
          synkad kopia
        </Badge>
      )}
    </div>
  )
}

function SfFact({ label, value, title, strong, tag }: {
  label: string
  value: string
  title?: string
  strong?: boolean
  tag?: string
}) {
  return (
    <span className="inline-flex items-baseline gap-1" title={title}>
      <span className="text-ink-500">{label}</span>
      <span className={cls(strong ? 'font-medium text-ink-1000' : 'text-ink-700')}>{value}</span>
      {tag && (
        <Badge variant="outline" icon={null} className="ml-0.5 h-4 border-ink-200 px-1 text-[9px] uppercase tracking-wide text-ink-500">
          {tag}
        </Badge>
      )}
    </span>
  )
}
